import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { SolitaireGame } from './SolitaireGame';
import { Store } from './store/gameStore';
import { SoundManager } from './core/soundManager';
import { GameName } from 'constants/difficulty';
import gsap from 'gsap';

import { findKlondikeMagic } from './gameMagicWand/KlondikeMagic';
import { MagicItem } from './gameMagicWand/magicGroup';
import { Point, Sprite } from 'pixi.js';
import { GAEvent, GAEventNames } from './analytics/events';
import { Card } from 'components/Card';
import { getAsset, onAssetsLoad } from './preloadGameAssets';
import { addToLevel, fadeUI } from './core/Playable2DRootContainer';
import { getScreenSize } from './core/screen';
import { Layout } from './LayoutManager';
import { MonoBehavior } from './core/monoBehavior';
import { MagicUIParticles } from './MagicUIParticles';
import { MagicGlow } from './MagicGlow';
import { MagicOverlay } from './MagicOverlay';
import { isMagicNeeded } from './gameMagicWand/isMagicNeeded';
import { GamePlace } from 'constants/cards';
import { ActionType } from './quests/types';
import { GameUI } from './gameUI/GameUI';
import { Popups } from 'ui/types/popups';
import { findSpiderMagic } from './gameMagicWand/SpiderMagic';
import { findFreeCellMagic } from './gameMagicWand/FreeCellMagic';

export class MagicWand extends MonoBehavior {
    game: SolitaireGame;
    active = false;
    magicWandSprite?: Sprite;
    activeParticle = false;
    magicCards: Card[] = [];
    constructor(game: SolitaireGame) {
        super();

        this.game = game;
        this.magicWand = this.magicWand.bind(this);
        GameEvent.add(EventName.MAGIC_WAND, this.magicWand);
        GameEvent.add(EventName.CLOSE_MODAL, this.modalClosed.bind(this));
        onAssetsLoad(this.preloadMagicWand.bind(this));
    }
    modalClosed({ id, confirm }: { id: string; confirm: boolean }) {
        if (id === 'magic-wand' && !confirm) {
            GAEvent(GAEventNames.MAGIC_WAND_ONBOARDING_CLOSE, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
        }
    }

    isAvailable() {
        GameEvent.emit(EventName.BLOCK_MAGIC_WAND, Store.tutorialActive || !isMagicNeeded(this.game));
    }

    async anim(tween: gsap.core.Tween) {
        return new Promise((resolve) => {
            tween.then(resolve);
        });
    }

    magicWand() {
        Store.startGame();
        if (!isMagicNeeded(this.game)) {
            GameEvent.emit(EventName.NO_MAGIC_KING_INFO);
            GameUI.showPopup(Popups.KING_POPUP, { textKey: 'noMagicNeeded', isNotification: true });
            return;
        }

        // if (!Store.hasPopup('magic-wand')) {
        //     GameUI.showPopup(Popups.MAGIC_WAND_POPUP);
        //     GAEvent(GAEventNames.MAGIC_WAND_ONBOARDING, {
        //         id: Store.gameId,
        //         type: Store.getGameTypeText(),
        //         difficulty: Store.getDifficultyText(),
        //     });
        //     return;
        // }

        let magicItems: (MagicItem | null)[] = [];

        switch (Store.name) {
            case GameName.KLONDIKE:
                magicItems = findKlondikeMagic(this.game);
                break;
            case GameName.SPIDER:
                magicItems = findSpiderMagic(this.game);
                break;
            case GameName.FREECELL:
                magicItems = findFreeCellMagic(this.game);
                break;
            // case GameName.PYRAMID:
            //     const pyramidMagic = new PyramidMagic(this.game);
            //     magicItem = pyramidMagic.findMagicMove();
            //     break;
        }

        const filteredMagicItem = magicItems.filter(Boolean) as MagicItem[];

        if (!filteredMagicItem.length) {
            this.showNoMagic();
        } else {
            const firstItem = filteredMagicItem[0];
            const secondItem = filteredMagicItem.length > 1 ? filteredMagicItem[1] : null;

            if (firstItem && secondItem) {
                const center = Layout.getBoardCenter();
                const { width } = Layout.getCardSize();

                const firstPosX = center.x + width * 1;
                const secondPosX = center.x - width * 1;


                const dx1 = secondPosX - firstItem.to.container.position.x;
                const dy1 = center.y - firstItem.to.container.position.y;
                const distance = Math.sqrt(dx1 * dx1 + dy1 * dy1);

                const dx2 = firstPosX - secondItem.to.container.position.x;
                const dy2 = center.y - secondItem.to.container.position.y;
                const distance2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

                if (distance < distance2) {
                    this.makeMagic([secondItem, firstItem]);
                    return;
                }
            }
            this.makeMagic(filteredMagicItem)
        }
    }

    showNoMagic() {
        // GameUI.showPopup(Popups.OUT_MOVE_WAND_POPUP);
        // GAEvent(GAEventNames.SHOW_NO_HINT_MODAL, {
        //     moves: Store.movesCount,
        //     id: Store.gameId,
        //     type: Store.getGameTypeText(),
        //     difficulty: Store.getDifficultyText(),
        // });
    }

    async preloadMagicWand() {
        const magicWandImage = await getAsset('MagicWand/magicWand');
        this.magicWandSprite = Sprite.from(magicWandImage);
        this.magicWandSprite.anchor.set(0.5, 0.3);
        this.magicWandSprite.zIndex = 10000;
        this.magicWandSprite.visible = false;
        addToLevel(this.magicWandSprite);
    }

    async playMagicWandAnimation(magicItems: Card[], callback: (targetCard: Card, index: number, isLast: boolean) => void) {
        if (!this.magicWandSprite) return;
        this.activeParticle = true;

        const fadeInTime = 0.7;
        const hitTime = 0.18;
        const fadeOutTime = 1.2;
        const { height } = getScreenSize();

        const cardSize = Layout.getCardSize();
        this.magicWandSprite.width = cardSize.width * 1.5;
        this.magicWandSprite.height = this.magicWandSprite.width;
        this.magicWandSprite.position.set(0, height * 0.6);
        this.magicWandSprite.visible = true;
        this.magicWandSprite.alpha = 1;

        let lastTargetPosition: Point = new Point(0, 0);
        for (let index = 0; index < magicItems.length; index++) {
            const magicItem = magicItems[index];
            const targetPosition = magicItem.container.position.clone();
            lastTargetPosition = targetPosition;
            const touchPoint = magicItem.place.cardPlace === GamePlace.TABLEAU ? cardSize.height * 0.3 : 0;


            gsap.to(this.magicWandSprite, { rotation: 1, duration: fadeInTime, ease: 'power1.easeOut' });

            await this.anim(gsap.to(this.magicWandSprite.position, {
                x: targetPosition.x - this.magicWandSprite.width * 0.1,
                y: targetPosition.y - cardSize.height * 1.0,
                duration: fadeInTime,
                ease: 'power1.easeOut',
            }))
            if (!this.magicWandSprite) return;

            this.anim(gsap.to(this.magicWandSprite, { rotation: 1.6, duration: hitTime, ease: 'power1.easeOut' }));


            // this.anim(gsap.to(this.magicWandSprite, {
            //     width: this.magicWandSprite.width,
            //     height: this.magicWandSprite.width,
            //     duration: hitTime,
            //     ease: 'power1.easeInOut',
            //     yoyo: true,
            //     repeat: 1,
            // }));

            this.anim(gsap.to(this.magicWandSprite, {
                rotation: 2,
                duration: hitTime,
                ease: 'power1.easeInOut',
                yoyo: true,
                repeat: 1,
            }));

            await this.anim(gsap.to(this.magicWandSprite.position, {
                x: targetPosition.x - this.magicWandSprite.width * 0.1,
                y: targetPosition.y - touchPoint - cardSize.height * 0.16,
                duration: hitTime,
                ease: 'power1.easeInOut',
            }));
            callback(magicItem, index, index === magicItems.length - 1);
        }

        // for (let index = 0; index < magicItems.length; index++) {
        //     const magicItem = magicItems[index];
        //     callback(magicItem, index, index === magicItems.length - 1);
        // }

        if (!this.magicWandSprite) return;
        this.activeParticle = false;

        this.anim(gsap.to(this.magicWandSprite.position, {
            x: lastTargetPosition.x - this.magicWandSprite.width * 2.7,
            y: lastTargetPosition.y,
            duration: fadeOutTime,
            ease: 'power1.easeIn',
        }));
        this.anim(gsap.to(this.magicWandSprite, {
            alpha: 0,
            rotation: 0.1,
            duration: fadeOutTime,
            ease: 'power1.easeIn',
        }));
    }

    makeMagic(magicItems: MagicItem[]) {
        this.active = true;
        Store.magicWandUsed++;
        this.game.blockMove(Infinity);
        GameEvent.emit(EventName.BLOCK_WAND_AND_HINTS, Infinity);
        Store.setWandCounter(Store.wandCounter - 1);
        GameEvent.emit(EventName.WAND_SHOW);
        SoundManager.play('magicWandActionSFX');

        this.magicCards = [];

        for (let i = 0; i < magicItems.length; i++) {
            const magicItem = magicItems[i];
            let { moveCard } = magicItem;
            this.magicCards.push(moveCard);
            new MagicGlow(moveCard);
            new MagicOverlay(moveCard);
        }

        fadeUI(0.6);
        
        const playAnimation = (magicItems: MagicItem[], onFinish: () => void) => {
            const allCards = magicItems.filter(Boolean).map(item => item.moveCard) as Card[];

            this.game.moves.add(allCards, false, true);
            
            this.playMagicWandAnimation(allCards, (targetCard: Card, index: number, isLast: boolean) => {
                // if (isLast) {
                //     this.game.moves.add(allCards, false, true);
                // }

                targetCard.takeUp();
                const center = Layout.getBoardCenter();
                const { width } = Layout.getCardSize();

                const posX = allCards.length > 1 ? 
                (index + 1) % 2 === 0 ? center.x - width * 1 : center.x + width * 1
                : center.x;
                gsap.to(targetCard.container.position, { x: posX, y: center.y, duration: 0.6 }).then(() => {
                    targetCard.setPosition(new Point(posX, center.y));
    
                    if (!targetCard.visible) {
                        if (targetCard.place.cardPlace === GamePlace.TABLEAU) {
                            GameEvent.emit(EventName.REVEAL_CARD, targetCard);
                            //revealCard in column, it will be added score in Klondike
                        }
                        targetCard.setVisible(true);
                    }
                    targetCard.setActive(true);
                    targetCard?.cardDisplay.glowMagic();
                    this.magicCards = this.magicCards.filter(c => c !== targetCard);
                });
                gsap.to(targetCard.cardDisplay.container.scale, { x: 1.2, y: 1.2, duration: 0.3, delay: 0.7 }).then(() => {
                    gsap.to(targetCard.cardDisplay.container.scale, { x: 1, y: 1, duration: 0.3 }).then(() => {
                        if (isLast) {
                            allCards.forEach(card => {
                                this.game.removeCardFromBoard(card);
                            });

                            this.game.dropMagicCards(magicItems, () => {
                                this.game.refreshBoard();
                                onFinish();
                            });
                            
                            fadeUI(1);
                        }
                    });
                });
            });
        }

        playAnimation(magicItems, () => {
            Store.increaseContextValue('magicWandUses').then((context) => {
                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.MAGIC_WAND_USES, payload: context, currentProgress: context.magicWandUses });
            }); 
        });
    }

    update() {
        if (this.activeParticle && this.magicWandSprite) {
            new MagicUIParticles(this.magicWandSprite, 2);
            for (const card of this.magicCards) {
                card.cardDisplay.showMagic();
            }
        }
    }
}
